import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const FrontOfficeBilling = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [billingData, setBillingData] = useState({
    pendingBills: [],
    pendingPayments: [],
    completedPayments: []
  });
  const [filterStatus, setFilterStatus] = useState('all');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showBillDetailsModal, setShowBillDetailsModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    method: 'cash',
    notes: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#3b82f6',
    purple: '#8b5cf6'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchBillingData();
    const interval = setInterval(fetchBillingData, 120000); // Refresh every 2 minutes
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setErrorMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  const fetchBillingData = async () => {
    setLoading(true);
    try {
      // Fetch current guests with pending bills
      const guestsData = await apiGet('/admin/stay-overview/current-guests');

      // Fetch all bookings
      const bookingsData = await apiGet('/admin/bookings');

      // Fetch all payments
      const paymentsData = await apiGet('/admin/payments');

      // Filter pending bills (bookings with payment_status not completed)
      const pendingBills = bookingsData.data?.filter(booking =>
        booking.payment_status === 'pending' || booking.payment_status === 'partial'
      ).map(booking => {
        const totalPaid = paymentsData.data
          ?.filter(p => p.booking_id === booking.id && p.payment_status === 'completed')
          .reduce((sum, p) => sum + parseFloat(p.amount), 0) || 0;

        const pendingAmount = parseFloat(booking.total_amount) - totalPaid;

        return {
          ...booking,
          total_paid: totalPaid,
          pending_amount: pendingAmount,
          days_pending: Math.floor((new Date() - new Date(booking.check_in_date)) / (1000 * 60 * 60 * 24))
        };
      }) || [];

      // Filter pending payments (payments with pending status)
      const pendingPayments = paymentsData.data?.filter(p =>
        p.payment_status === 'pending'
      ).map(payment => {
        const booking = bookingsData.data?.find(b => b.id === payment.booking_id);
        return {
          ...payment,
          booking_details: booking
        };
      }) || [];

      // Completed payments
      const completedPayments = paymentsData.data?.filter(p =>
        p.payment_status === 'completed'
      ).map(payment => {
        const booking = bookingsData.data?.find(b => b.id === payment.booking_id);
        return {
          ...payment,
          booking_details: booking
        };
      }) || [];

      setBillingData({
        pendingBills,
        pendingPayments,
        completedPayments
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching billing data:', error);
      setErrorMessage('Failed to load billing data');
      setLoading(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    if (!selectedBooking) return;

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          booking_id: selectedBooking.id,
          amount: parseFloat(paymentForm.amount),
          payment_method: paymentForm.method,
          payment_status: 'completed',
          notes: paymentForm.notes
        }),
      });

      if (response.ok) {
        const data = await response.json();

        setSuccessMessage('Payment recorded successfully! Receipt generated.');
        setShowPaymentModal(false);
        setPaymentForm({ amount: '', method: 'cash', notes: '' });
        setSelectedBooking(null);
        fetchBillingData();

        // Auto-download receipt if payment was created successfully
        if (data.success && data.data.payment_id) {
          // Small delay to ensure receipt is ready
          setTimeout(() => {
            const receiptUrl = `${PRODUCTION_URL}/api/admin/payments/${data.data.payment_id}/receipt`;
            window.open(receiptUrl, '_blank');
          }, 500);
        }
      } else {
        setErrorMessage('Failed to record payment');
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      setErrorMessage('Error processing payment');
    }
  };

  const generatePDF = (data, type = 'all') => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    // Header
    doc.setFillColor(45, 80, 22);
    doc.rect(0, 0, pageWidth, 35, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont(undefined, 'bold');
    doc.text('Hotel Management System', pageWidth / 2, 15, { align: 'center' });

    doc.setFontSize(14);
    doc.setFont(undefined, 'normal');
    doc.text('Billing Report', pageWidth / 2, 25, { align: 'center' });

    // Date
    doc.setFontSize(10);
    const currentDate = new Date().toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    doc.text(`Generated: ${currentDate}`, pageWidth / 2, 32, { align: 'center' });

    let yPosition = 45;

    // Summary Section
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Summary', 14, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');

    const totalPendingBills = data.pendingBills.length;
    const totalPendingAmount = data.pendingBills.reduce((sum, bill) => sum + bill.pending_amount, 0);
    const totalPendingPayments = data.pendingPayments.length;
    const totalCompletedPayments = data.completedPayments.length;
    const totalCompletedAmount = data.completedPayments.reduce((sum, payment) => sum + parseFloat(payment.amount), 0);

    const summaryData = [
      ['Total Pending Bills', totalPendingBills.toString()],
      ['Total Pending Amount', `₹${totalPendingAmount.toFixed(2)}`],
      ['Pending Payments Count', totalPendingPayments.toString()],
      ['Completed Payments Count', totalCompletedPayments.toString()],
      ['Total Collected Amount', `₹${totalCompletedAmount.toFixed(2)}`]
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [['Metric', 'Value']],
      body: summaryData,
      theme: 'grid',
      headStyles: { fillColor: [45, 80, 22], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 100 },
        1: { halign: 'right' }
      }
    });

    yPosition = doc.lastAutoTable.finalY + 12;

    // Pending Bills Section
    if (type === 'all' || type === 'pending') {
      if (yPosition > pageHeight - 60) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Pending Bills', 14, yPosition);
      yPosition += 5;

      if (data.pendingBills.length > 0) {
        const pendingBillsData = data.pendingBills.map(bill => [
          bill.guest_name,
          bill.room_id || 'N/A',
          bill.check_in_date ? new Date(bill.check_in_date).toLocaleDateString('en-IN') : 'N/A',
          `₹${parseFloat(bill.total_amount).toFixed(2)}`,
          `₹${bill.total_paid.toFixed(2)}`,
          `₹${bill.pending_amount.toFixed(2)}`,
          bill.payment_status.toUpperCase()
        ]);

        autoTable(doc, {
          startY: yPosition,
          head: [['Guest Name', 'Room', 'Check-in', 'Total', 'Paid', 'Pending', 'Status']],
          body: pendingBillsData,
          theme: 'striped',
          headStyles: { fillColor: [45, 80, 22], textColor: 255, fontStyle: 'bold' },
          styles: { fontSize: 8, cellPadding: 2 },
          columnStyles: {
            0: { cellWidth: 35 },
            1: { cellWidth: 20, halign: 'center' },
            2: { cellWidth: 25, halign: 'center' },
            3: { cellWidth: 25, halign: 'right' },
            4: { cellWidth: 25, halign: 'right' },
            5: { cellWidth: 25, halign: 'right' },
            6: { cellWidth: 25, halign: 'center' }
          }
        });

        yPosition = doc.lastAutoTable.finalY + 12;
      } else {
        doc.setFontSize(10);
        doc.setFont(undefined, 'italic');
        doc.text('No pending bills found', 14, yPosition + 5);
        yPosition += 17;
      }
    }

    // Completed Payments Section
    if (type === 'all' || type === 'completed') {
      if (yPosition > pageHeight - 60) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Recent Completed Payments', 14, yPosition);
      yPosition += 5;

      if (data.completedPayments.length > 0) {
        const recentPayments = data.completedPayments.slice(0, 20); // Show latest 20
        const completedPaymentsData = recentPayments.map(payment => [
          payment.booking_details?.guest_name || 'N/A',
          payment.booking_details?.room_id || 'N/A',
          `₹${parseFloat(payment.amount).toFixed(2)}`,
          payment.payment_method.toUpperCase(),
          new Date(payment.payment_date).toLocaleDateString('en-IN'),
          payment.payment_status.toUpperCase()
        ]);

        autoTable(doc, {
          startY: yPosition,
          head: [['Guest Name', 'Room', 'Amount', 'Method', 'Date', 'Status']],
          body: completedPaymentsData,
          theme: 'striped',
          headStyles: { fillColor: [74, 124, 42], textColor: 255, fontStyle: 'bold' },
          styles: { fontSize: 8, cellPadding: 2 },
          columnStyles: {
            0: { cellWidth: 40 },
            1: { cellWidth: 20, halign: 'center' },
            2: { cellWidth: 30, halign: 'right' },
            3: { cellWidth: 25, halign: 'center' },
            4: { cellWidth: 30, halign: 'center' },
            5: { cellWidth: 25, halign: 'center' }
          }
        });
      } else {
        doc.setFontSize(10);
        doc.setFont(undefined, 'italic');
        doc.text('No completed payments found', 14, yPosition + 5);
      }
    }

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
      doc.text(
        'Generated by Hotel Management System',
        pageWidth - 14,
        pageHeight - 10,
        { align: 'right' }
      );
    }

    // Auto download
    const fileName = `Billing_Report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    setSuccessMessage('PDF downloaded successfully!');
  };

  const handleDownloadPendingBills = () => {
    generatePDF(billingData, 'pending');
  };

  const handleDownloadCompletedPayments = () => {
    generatePDF(billingData, 'completed');
  };

  const handleDownloadAllBilling = () => {
    generatePDF(billingData, 'all');
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return colors.success;
      case 'partial':
        return colors.warning;
      case 'pending':
        return colors.danger;
      default:
        return colors.textLight;
    }
  };

  const getFilteredPendingBills = () => {
    let filtered = [...billingData.pendingBills];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(bill =>
        bill.guest_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bill.guest_phone?.includes(searchTerm) ||
        bill.room_id?.toString().includes(searchTerm)
      );
    }

    // Date filter
    if (dateFilter.startDate) {
      filtered = filtered.filter(bill =>
        new Date(bill.check_in_date) >= new Date(dateFilter.startDate)
      );
    }
    if (dateFilter.endDate) {
      filtered = filtered.filter(bill =>
        new Date(bill.check_in_date) <= new Date(dateFilter.endDate)
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(bill => bill.payment_status === filterStatus);
    }

    return filtered;
  };

  const styles = {
    container: {
      padding: isMobile ? '16px' : '24px',
      background: colors.lightGray,
      minHeight: '100vh'
    },
    pageHeader: {
      background: colors.white,
      padding: isMobile ? '20px' : '28px',
      borderRadius: isMobile ? '14px' : '18px',
      marginBottom: isMobile ? '20px' : '28px',
      boxShadow: '0 6px 20px rgba(45, 80, 22, 0.1)',
      border: `2px solid ${colors.mediumGray}`
    },
    pageTitle: {
      fontSize: isMobile ? '22px' : '32px',
      fontWeight: '800',
      color: colors.primary,
      marginBottom: '6px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    pageSubtitle: {
      fontSize: isMobile ? '13px' : '15px',
      color: colors.textLight,
      fontWeight: '500'
    },
    actionBar: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '12px',
      marginTop: '16px',
      justifyContent: isMobile ? 'flex-start' : 'space-between'
    },
    refreshButton: {
      padding: isMobile ? '8px 16px' : '10px 20px',
      background: colors.primary,
      color: colors.white,
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '600',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 8px rgba(45, 80, 22, 0.2)'
    },
    downloadButton: {
      padding: isMobile ? '8px 16px' : '10px 20px',
      background: colors.success,
      color: colors.white,
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '600',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 8px rgba(74, 124, 42, 0.2)'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      gap: isMobile ? '12px' : '16px',
      marginBottom: isMobile ? '24px' : '32px',
      padding: isMobile ? '0 8px' : '0'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '20px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 4px 16px rgba(45, 80, 22, 0.08)',
      border: `2px solid ${colors.mediumGray}`,
      transition: 'all 0.3s ease'
    },
    statValue: {
      fontSize: isMobile ? '24px' : '32px',
      fontWeight: '800',
      marginBottom: '6px',
      lineHeight: '1'
    },
    statLabel: {
      fontSize: isMobile ? '10px' : '11px',
      color: colors.textLight,
      textTransform: 'uppercase',
      fontWeight: '700',
      letterSpacing: '0.8px'
    },
    statIcon: {
      fontSize: isMobile ? '20px' : '24px',
      marginBottom: '8px',
      display: 'block'
    },
    searchBar: {
      padding: isMobile ? '10px 14px' : '12px 16px',
      borderRadius: '8px',
      border: `2px solid ${colors.mediumGray}`,
      fontSize: isMobile ? '13px' : '14px',
      width: isMobile ? '100%' : '300px',
      transition: 'all 0.3s ease'
    },
    filterContainer: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
      marginBottom: '16px'
    },
    filterButton: {
      padding: isMobile ? '6px 12px' : '8px 16px',
      borderRadius: '8px',
      border: `1.5px solid ${colors.mediumGray}`,
      background: colors.white,
      fontSize: isMobile ? '11px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      color: colors.textDark
    },
    activeFilter: {
      background: colors.primary,
      color: colors.white,
      borderColor: colors.primary
    },
    sectionCard: {
      background: colors.white,
      padding: isMobile ? '18px' : '24px',
      borderRadius: isMobile ? '14px' : '18px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.1)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 8px 16px' : '0 0 28px',
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: isMobile ? '16px' : '20px',
      flexWrap: 'wrap',
      gap: '12px'
    },
    sectionTitle: {
      fontSize: isMobile ? '18px' : '22px',
      fontWeight: '700',
      color: colors.textDark,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '11px' : '14px',
      overflowX: 'auto',
      display: isMobile ? 'block' : 'table'
    },
    tableHeader: {
      background: colors.lightGray,
      fontWeight: '700',
      textAlign: 'left',
      padding: isMobile ? '10px' : '14px',
      borderBottom: `2px solid ${colors.mediumGray}`,
      color: colors.textDark,
      fontSize: isMobile ? '10px' : '13px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    tableCell: {
      padding: isMobile ? '10px' : '14px',
      borderBottom: `1px solid ${colors.lightGray}`,
      verticalAlign: 'middle'
    },
    badge: {
      padding: '5px 12px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: '700',
      display: 'inline-block',
      textTransform: 'uppercase',
      letterSpacing: '0.3px'
    },
    button: {
      padding: isMobile ? '7px 14px' : '9px 18px',
      borderRadius: '7px',
      border: 'none',
      fontSize: isMobile ? '11px' : '13px',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textTransform: 'uppercase',
      letterSpacing: '0.3px'
    },
    primaryButton: {
      background: colors.primary,
      color: colors.white,
      boxShadow: '0 2px 8px rgba(45, 80, 22, 0.3)'
    },
    successButton: {
      background: colors.success,
      color: colors.white,
      boxShadow: '0 2px 8px rgba(74, 124, 42, 0.3)'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      background: colors.white,
      padding: isMobile ? '20px' : '32px',
      borderRadius: '16px',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
    },
    modalHeader: {
      fontSize: isMobile ? '20px' : '24px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '20px',
      paddingBottom: '16px',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '600',
      color: colors.textDark,
      marginBottom: '8px'
    },
    input: {
      width: '100%',
      padding: '12px',
      borderRadius: '8px',
      border: `2px solid ${colors.mediumGray}`,
      fontSize: '14px',
      transition: 'all 0.3s ease'
    },
    select: {
      width: '100%',
      padding: '12px',
      borderRadius: '8px',
      border: `2px solid ${colors.mediumGray}`,
      fontSize: '14px',
      background: colors.white,
      cursor: 'pointer'
    },
    textarea: {
      width: '100%',
      padding: '12px',
      borderRadius: '8px',
      border: `2px solid ${colors.mediumGray}`,
      fontSize: '14px',
      minHeight: '80px',
      resize: 'vertical'
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px',
      marginTop: '24px',
      justifyContent: 'flex-end'
    },
    cancelButton: {
      background: colors.mediumGray,
      color: colors.textDark
    },
    alertSuccess: {
      background: '#d1fae5',
      border: `2px solid ${colors.success}`,
      color: '#065f46',
      padding: '12px 16px',
      borderRadius: '8px',
      marginBottom: '20px',
      fontSize: '14px',
      fontWeight: '600'
    },
    alertError: {
      background: '#fee2e2',
      border: `2px solid ${colors.danger}`,
      color: '#991b1b',
      padding: '12px 16px',
      borderRadius: '8px',
      marginBottom: '20px',
      fontSize: '14px',
      fontWeight: '600'
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: colors.textLight,
      fontSize: '14px'
    }
  };

  const filteredPendingBills = getFilteredPendingBills();

  const totalStats = {
    pendingBills: billingData.pendingBills.length,
    totalPendingAmount: billingData.pendingBills.reduce((sum, bill) => sum + bill.pending_amount, 0),
    totalCompletedPayments: billingData.completedPayments.length,
    totalCollectedAmount: billingData.completedPayments.reduce((sum, payment) => sum + parseFloat(payment.amount), 0)
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ ...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <p style={{ fontSize: '18px', color: colors.textLight }}>Loading billing data...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={styles.container}>
        {/* Success/Error Messages */}
        {successMessage && (
          <div style={styles.alertSuccess}>
            ✓ {successMessage}
          </div>
        )}
        {errorMessage && (
          <div style={styles.alertError}>
            ✗ {errorMessage}
          </div>
        )}

        {/* Page Header */}
        <div style={styles.pageHeader}>
          <h1 style={styles.pageTitle}>
            <span>💰</span>
            Billing & Payments
          </h1>
          <p style={styles.pageSubtitle}>
            Manage pending bills, payments, and generate billing reports
          </p>
          <div style={styles.actionBar}>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                style={styles.refreshButton}
                onClick={fetchBillingData}
              >
                <span>🔄</span>
                Refresh Data
              </button>
              <button
                style={styles.downloadButton}
                onClick={handleDownloadAllBilling}
              >
                <span>📄</span>
                Download All Report
              </button>
              <button
                style={{ ...styles.downloadButton, background: colors.warning }}
                onClick={handleDownloadPendingBills}
              >
                <span>⚠️</span>
                Pending Bills PDF
              </button>
              <button
                style={styles.downloadButton}
                onClick={handleDownloadCompletedPayments}
              >
                <span>✓</span>
                Completed Payments PDF
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>📋</div>
            <div style={{ ...styles.statValue, color: colors.danger }}>
              {totalStats.pendingBills}
            </div>
            <div style={styles.statLabel}>Pending Bills</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statIcon}>💸</div>
            <div style={{ ...styles.statValue, color: colors.danger }}>
              {formatCurrency(totalStats.totalPendingAmount)}
            </div>
            <div style={styles.statLabel}>Total Pending</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statIcon}>✅</div>
            <div style={{ ...styles.statValue, color: colors.success }}>
              {totalStats.totalCompletedPayments}
            </div>
            <div style={styles.statLabel}>Completed Payments</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statIcon}>💰</div>
            <div style={{ ...styles.statValue, color: colors.success }}>
              {formatCurrency(totalStats.totalCollectedAmount)}
            </div>
            <div style={styles.statLabel}>Total Collected</div>
          </div>
        </div>

        {/* Pending Bills Section */}
        <div style={styles.sectionCard}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              <span>⚠️</span>
              Pending Bills
            </h2>
          </div>

          {/* Search and Filters */}
          <div style={{ marginBottom: '20px' }}>
            <input
              type="text"
              placeholder="Search by guest name, phone, or room..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchBar}
            />
          </div>

          <div style={styles.filterContainer}>
            <button
              style={{
                ...styles.filterButton,
                ...(filterStatus === 'all' ? styles.activeFilter : {})
              }}
              onClick={() => setFilterStatus('all')}
            >
              All
            </button>
            <button
              style={{
                ...styles.filterButton,
                ...(filterStatus === 'pending' ? styles.activeFilter : {})
              }}
              onClick={() => setFilterStatus('pending')}
            >
              Pending
            </button>
            <button
              style={{
                ...styles.filterButton,
                ...(filterStatus === 'partial' ? styles.activeFilter : {})
              }}
              onClick={() => setFilterStatus('partial')}
            >
              Partial
            </button>
          </div>

          {filteredPendingBills.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>Guest Name</th>
                    <th style={styles.tableHeader}>Room</th>
                    <th style={styles.tableHeader}>Check-in</th>
                    <th style={styles.tableHeader}>Total Amount</th>
                    <th style={styles.tableHeader}>Paid</th>
                    <th style={styles.tableHeader}>Pending</th>
                    <th style={styles.tableHeader}>Status</th>
                    <th style={styles.tableHeader}>Days Pending</th>
                    <th style={styles.tableHeader}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPendingBills.map((bill) => (
                    <tr key={bill.id}>
                      <td style={styles.tableCell}>
                        <div style={{ fontWeight: '600' }}>{bill.guest_name}</div>
                        <div style={{ fontSize: '11px', color: colors.textLight }}>
                          {bill.guest_phone}
                        </div>
                      </td>
                      <td style={styles.tableCell}>{bill.room_id || 'N/A'}</td>
                      <td style={styles.tableCell}>{formatDate(bill.check_in_date)}</td>
                      <td style={styles.tableCell}>{formatCurrency(bill.total_amount)}</td>
                      <td style={styles.tableCell}>
                        <span style={{ color: colors.success, fontWeight: '600' }}>
                          {formatCurrency(bill.total_paid)}
                        </span>
                      </td>
                      <td style={styles.tableCell}>
                        <span style={{ color: colors.danger, fontWeight: '700' }}>
                          {formatCurrency(bill.pending_amount)}
                        </span>
                      </td>
                      <td style={styles.tableCell}>
                        <span
                          style={{
                            ...styles.badge,
                            background: `${getStatusColor(bill.payment_status)}20`,
                            color: getStatusColor(bill.payment_status)
                          }}
                        >
                          {bill.payment_status}
                        </span>
                      </td>
                      <td style={styles.tableCell}>
                        <span style={{
                          color: bill.days_pending > 7 ? colors.danger : colors.warning,
                          fontWeight: '600'
                        }}>
                          {bill.days_pending} days
                        </span>
                      </td>
                      <td style={styles.tableCell}>
                        <button
                          style={{ ...styles.button, ...styles.primaryButton }}
                          onClick={() => {
                            setSelectedBooking(bill);
                            setPaymentForm({ ...paymentForm, amount: bill.pending_amount.toString() });
                            setShowPaymentModal(true);
                          }}
                        >
                          Add Payment
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={styles.emptyState}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
              <p>No pending bills found</p>
            </div>
          )}
        </div>

        {/* Recent Completed Payments Section */}
        <div style={styles.sectionCard}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              <span>✅</span>
              Recent Completed Payments
            </h2>
          </div>

          {billingData.completedPayments.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>Guest Name</th>
                    <th style={styles.tableHeader}>Room</th>
                    <th style={styles.tableHeader}>Amount</th>
                    <th style={styles.tableHeader}>Method</th>
                    <th style={styles.tableHeader}>Payment Date</th>
                    <th style={styles.tableHeader}>Receipt</th>
                    <th style={styles.tableHeader}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {billingData.completedPayments.slice(0, 10).map((payment) => (
                    <tr key={payment.id}>
                      <td style={styles.tableCell}>
                        <div style={{ fontWeight: '600' }}>
                          {payment.booking_details?.guest_name || 'N/A'}
                        </div>
                      </td>
                      <td style={styles.tableCell}>
                        {payment.booking_details?.room_id || 'N/A'}
                      </td>
                      <td style={styles.tableCell}>
                        <span style={{ color: colors.success, fontWeight: '700' }}>
                          {formatCurrency(payment.amount)}
                        </span>
                      </td>
                      <td style={styles.tableCell}>
                        <span
                          style={{
                            ...styles.badge,
                            background: `${colors.info}20`,
                            color: colors.info
                          }}
                        >
                          {payment.payment_method}
                        </span>
                      </td>
                      <td style={styles.tableCell}>
                        {formatDate(payment.payment_date)}
                      </td>
                      <td style={styles.tableCell}>
                        {payment.receipt_number ? (
                          <button
                            style={{ ...styles.button, ...styles.successButton, fontSize: '11px', padding: '6px 12px' }}
                            onClick={() => window.open(`${PRODUCTION_URL}/api/admin/payments/${payment.id}/receipt`, '_blank')}
                            title="Download Receipt"
                          >
                            📄 Download
                          </button>
                        ) : (
                          <span style={{ color: colors.textLight, fontSize: '11px' }}>No receipt</span>
                        )}
                      </td>
                      <td style={styles.tableCell}>
                        <span
                          style={{
                            ...styles.badge,
                            background: `${colors.success}20`,
                            color: colors.success
                          }}
                        >
                          {payment.payment_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={styles.emptyState}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
              <p>No completed payments yet</p>
            </div>
          )}
        </div>

        {/* Payment Modal */}
        {showPaymentModal && selectedBooking && (
          <div style={styles.modal} onClick={() => setShowPaymentModal(false)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <h2 style={styles.modalHeader}>Add Payment</h2>

              <div style={{ marginBottom: '20px', padding: '16px', background: colors.lightGray, borderRadius: '8px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Guest:</strong> {selectedBooking.guest_name}
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Room:</strong> {selectedBooking.room_id}
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Total Amount:</strong> {formatCurrency(selectedBooking.total_amount)}
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Already Paid:</strong> {formatCurrency(selectedBooking.total_paid)}
                </div>
                <div>
                  <strong style={{ color: colors.danger }}>Pending:</strong>{' '}
                  <span style={{ color: colors.danger, fontWeight: '700' }}>
                    {formatCurrency(selectedBooking.pending_amount)}
                  </span>
                </div>
              </div>

              <form onSubmit={handlePayment}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Payment Amount *</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                    <input
                      type="number"
                      step="0.01"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                      style={{ ...styles.input, flex: 1 }}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => {
                        // Calculate full stay amount: nights × price per night
                        const checkIn = new Date(selectedBooking.check_in_date);
                        checkIn.setHours(0, 0, 0, 0);
                        const checkOut = new Date(selectedBooking.check_out_date);
                        checkOut.setHours(0, 0, 0, 0);

                        // Calculate nights (difference in days)
                        const diffTime = checkOut.getTime() - checkIn.getTime();
                        const nights = Math.max(1, Math.round(diffTime / (1000 * 60 * 60 * 24)));

                        // total_amount is the price per night, so multiply by nights
                        const pricePerNight = parseFloat(selectedBooking.total_amount);
                        const fullStayAmount = nights * pricePerNight;

                        setPaymentForm({ ...paymentForm, amount: fullStayAmount.toFixed(2) });
                      }}
                      style={{
                        padding: '12px 16px',
                        background: colors.success,
                        color: colors.white,
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.3s ease'
                      }}
                      title="Pay for full stay duration"
                    >
                      💰 Pay Full
                    </button>
                  </div>
                  <div style={{ fontSize: '12px', color: colors.textLight, marginTop: '6px' }}>
                    Click "Pay Full" to pay for the entire stay (
                    {(() => {
                      const checkIn = new Date(selectedBooking.check_in_date);
                      checkIn.setHours(0, 0, 0, 0);
                      const checkOut = new Date(selectedBooking.check_out_date);
                      checkOut.setHours(0, 0, 0, 0);
                      const diffTime = checkOut.getTime() - checkIn.getTime();
                      const nights = Math.max(1, Math.round(diffTime / (1000 * 60 * 60 * 24)));
                      const pricePerNight = parseFloat(selectedBooking.total_amount);
                      const fullAmount = nights * pricePerNight;
                      return `${nights} night${nights > 1 ? 's' : ''} × ₹${pricePerNight.toLocaleString('en-IN')} = ₹${fullAmount.toLocaleString('en-IN')}`;
                    })()}
                    )
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Payment Method *</label>
                  <select
                    value={paymentForm.method}
                    onChange={(e) => setPaymentForm({ ...paymentForm, method: e.target.value })}
                    style={styles.select}
                    required
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="upi">UPI</option>
                    <option value="online">Online Transfer</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Notes (Optional)</label>
                  <textarea
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    style={styles.textarea}
                    placeholder="Add any notes about this payment..."
                  />
                </div>

                <div style={styles.buttonGroup}>
                  <button
                    type="button"
                    style={{ ...styles.button, ...styles.cancelButton }}
                    onClick={() => {
                      setShowPaymentModal(false);
                      setPaymentForm({ amount: '', method: 'cash', notes: '' });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{ ...styles.button, ...styles.successButton }}
                  >
                    Record Payment
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default FrontOfficeBilling;